import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { ZoomIn, ZoomOut, RotateCcw, Trash2, Loader2, ImagePlus, Move } from "lucide-react";

const DEFAULT_BG = "https://images.unsplash.com/photo-1469371670807-013ccf25f16a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

// Parses "50% 40%" -> {x:50,y:40}; also maps top/center/bottom keywords
const parsePos = (p) => {
  if (!p || p === "center") return { x: 50, y: 50 };
  if (p === "top") return { x: 50, y: 0 };
  if (p === "bottom") return { x: 50, y: 100 };
  const m = String(p).match(/(\d{1,3})%\s+(\d{1,3})%/);
  return m ? { x: clamp(+m[1], 0, 100), y: clamp(+m[2], 0, 100) } : { x: 50, y: 50 };
};

export const PhotoTransformDialog = ({ open, onOpenChange, currentSrc, hasPhoto, initialPosition, initialZoom, onSave, onDelete }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(100);
  const [busy, setBusy] = useState(false);
  const dragRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFile(null); setPreview(null);
      setPos(parsePos(initialPosition));
      setZoom(clamp(Number(initialZoom) || 100, 100, 300));
    }
  }, [open, initialPosition, initialZoom]);

  const src = preview || currentSrc || DEFAULT_BG;
  const hasImage = Boolean(preview || currentSrc);

  const pickFile = (fl) => {
    const f = (fl || [])[0]; if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setPos({ x: 50, y: 50 }); setZoom(100);
  };

  const onPointerDown = (e) => {
    if (!hasImage) return;
    const rect = boxRef.current.getBoundingClientRect();
    dragRef.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y, w: rect.width, h: rect.height };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    // extra pannable range grows with zoom
    const range = Math.max(1, (zoom - 100)) / 1.2 + 60;
    const nx = clamp(d.px - ((e.clientX - d.x) / d.w) * range, 0, 100);
    const ny = clamp(d.py - ((e.clientY - d.y) / d.h) * range, 0, 100);
    setPos({ x: Math.round(nx), y: Math.round(ny) });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const reset = () => { setPos({ x: 50, y: 50 }); setZoom(100); };

  const save = async () => {
    setBusy(true);
    try {
      await onSave({ file, position: `${pos.x}% ${pos.y}%`, zoom });
      onOpenChange(false);
    } finally { setBusy(false); }
  };
  const del = async () => {
    setBusy(true);
    try { await onDelete(); onOpenChange(false); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="photo-transform-dialog">
        <DialogHeader><DialogTitle className="font-heading text-burgundy">Atur Foto Portal</DialogTitle>
          <DialogDescription className="text-xs text-[#7A7A7A]">Pilih foto, atur zoom & posisi, lalu simpan sebagai foto portal Anda.</DialogDescription>
        </DialogHeader>
        <div
          ref={boxRef}
          className="relative w-full rounded-xl overflow-hidden bg-beige border border-gold/30 cursor-move select-none touch-none"
          style={{ aspectRatio: "21 / 9" }}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
          data-testid="photo-transform-canvas"
        >
          <img src={src} alt="preview" draggable={false}
            style={{ objectPosition: `${pos.x}% ${pos.y}%`, transform: `scale(${zoom / 100})` }}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
          {hasImage && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/45 text-white text-[11px]">
              <Move className="w-3 h-3" />Geser untuk atur posisi
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <ZoomOut className="w-4 h-4 text-burgundy shrink-0" />
          <Slider value={[zoom]} min={100} max={300} step={5} onValueChange={(v) => setZoom(v[0])} disabled={!hasImage} className="flex-1" data-testid="photo-zoom-slider" />
          <ZoomIn className="w-4 h-4 text-burgundy shrink-0" />
          <span className="text-xs font-mono text-[#7A7A7A] w-10 text-right">{zoom}%</span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-full bg-beige overflow-hidden border border-gold/20" data-testid="photo-pos-quick">
            {[["top", "Atas", 0], ["center", "Tengah", 50], ["bottom", "Bawah", 100]].map(([k, lbl, y]) => (
              <button key={k} onClick={() => setPos((p) => ({ ...p, y }))} disabled={!hasImage}
                className="px-3 py-1.5 text-xs font-semibold text-burgundy hover:bg-gold/20 disabled:opacity-40" data-testid={`photo-pos-${k}`}>{lbl}</button>
            ))}
          </div>
          <button onClick={reset} disabled={!hasImage} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-gold/30 text-burgundy hover:bg-beige disabled:opacity-40" data-testid="photo-reset"><RotateCcw className="w-3.5 h-3.5" />Reset</button>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <label className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full bg-beige text-burgundy font-semibold cursor-pointer hover:bg-gold/20" data-testid="photo-choose">
            <ImagePlus className="w-4 h-4" />{hasImage ? "Pilih Foto Lain" : "Pilih Foto"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { pickFile(e.target.files); e.target.value = ""; }} />
          </label>
          {hasPhoto && (
            <button onClick={del} disabled={busy} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40" data-testid="photo-delete"><Trash2 className="w-4 h-4" />Hapus Foto</button>
          )}
          <Button onClick={save} disabled={busy || !hasImage} className="ml-auto bg-burgundy hover:bg-burgundy-dark text-white rounded-full" data-testid="photo-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
